import type { Filter } from '@unifyapps/defs/types/query';
import { Op } from '@unifyapps/network/generated/models/op';
import { Order } from '@unifyapps/network/generated/models/order';
import { getFindNotificationsQueryKey } from '@unifyapps/network/generated/notification-rest-api/notification-rest-api';
import { useQueryClient } from '@unifyapps/network/react-query';
import _compact from 'lodash/compact';
import { useCallback, useMemo } from 'react';
import { useNotificationsInfiniteQuery } from './useNotificationsInfiniteQuery';
import { useUnreadNotificationsInfiniteQuery } from './useSearchUnreadInfiniteQuery';

const getFilters = ({ filter }: { filter: string }) => {
  const filters: Filter[] = [];

  if (filter !== 'ALL') {
    filters.push({
      field: 'tags',
      op: Op.IN,
      values: [filter],
    });
  }

  return filters;
};

const getNotificationsQuery = ({ userId, filter }: { userId: number; filter: string }) => ({
  userId,
  filter:
    filter === 'ALL'
      ? undefined
      : {
          op: Op.AND,
          values: [
            {
              op: Op.AND,
              values: getFilters({ filter }),
            },
          ],
        },
  sorts: [{ field: 'cTm', order: Order.DESC }],
});

export const useNotifications = ({
  userId,
  filter,
  unread,
}: {
  userId: number;
  filter: string;
  unread: boolean;
}) => {
  const queryClient = useQueryClient();

  const notificationsQuery = useMemo(
    () => getNotificationsQuery({ userId, filter }),
    [userId, filter],
  );

  const allNotificationsResponse = useNotificationsInfiniteQuery(notificationsQuery, undefined, {
    enabled: !unread,
  });

  const unreadNotificationsResponse = useUnreadNotificationsInfiniteQuery(
    notificationsQuery,
    undefined,
    {
      enabled: unread,
    },
  );

  const {
    data,
    fetchNextPage: _fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
    error,
  } = unread ? unreadNotificationsResponse : allNotificationsResponse;

  const notifications = useMemo(
    () => (data?.pages ? _compact(data.pages.flatMap((page) => page.objects)) : []),
    [data?.pages],
  );

  const fetchNextPage = useCallback(() => {
    if (hasNextPage && !isFetching) {
      void _fetchNextPage();
    }
  }, [_fetchNextPage, hasNextPage, isFetching]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: getFindNotificationsQueryKey(notificationsQuery),
    });
  }, [notificationsQuery, queryClient]);

  return {
    notifications,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isLoading,
    refetch,
    error,
    invalidate,
  };
};
